import { ChevronLeft, ChevronRight, ChevronDown, UploadCloud as CloudUpload, ShieldPlus, Settings, LifeBuoy, LogOut, Flag, Briefcase } from 'lucide-react';
import { MoonIcon } from '@heroicons/react/24/solid';
import React from 'react';

// Custom filled Panels icon used for the Top Menu (keeps `currentColor` for easy theming)
function PanelsTopLeftFilled({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M3 9h18V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4Z" fill="currentColor" stroke="none" />
      <path d="M9 21V9" />
    </svg>
  );
}
import Tooltip from '../ui/Tooltip';
import AvatarButton, { AVATAR_BUTTON_DIAMETERS, AVATAR_CHEVRON_DIAMETERS } from '../ui/AvatarButton';
import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import LightbulbSolid from '../icons/LightbulbSolid';
import type { PageName } from '../../types';
import { NAV_ITEMS } from '../../constants';
import { TTLogo } from '../ui/Logo';
import NavItem from './NavItem';
import { signOut } from '../../lib/auth.ts';
import { getAccountDisplayName } from '../../lib/accountDisplay';
import { currentRuntime } from '../../config/runtime';
import { resolveSidebarCollapsedLogo, resolveSidebarExpandedLogo, type WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';
import { getTextLogoInitials, getTextLogoName } from '../../lib/textLogo';

// Icon size helpers
const ACTION_ICON = 'w-4 h-4';
const SIDEBAR_DIVIDER_TOP_MARGIN_BY_TENANT: Record<string, string> = {
  bearbull: 'mt-4',
};
const SIDEBAR_NAV_TOP_PADDING_BY_TENANT: Record<string, string> = {
  bearbull: 'pt-4',
};

type SidebarProps = {
  active: PageName;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  onNavigate: (name: PageName) => void;
  onLogoutStart?: () => void;
  displayName?: string | null;
  email?: string | null;
  locationLabel?: string | null;
  theme: 'light' | 'dark';
  setTheme?: (theme: 'light' | 'dark') => void;
  canSwitchTheme?: boolean;
  avatarUrl?: string | null;
  showAdminTools?: boolean;
  /** Show the Business (Partner Portal) item. Server-authorized; true for platform
   *  admins and tenant business admins (showBusiness from the app). */
  showBusiness?: boolean;
  whiteLabel?: WhiteLabelAppBranding | null;
  setNavLayout?: (layout: 'sidebar' | 'top') => void;
};

export function Sidebar({ active, collapsed, setCollapsed, onNavigate, onLogoutStart, displayName, email, locationLabel, theme, setTheme, canSwitchTheme = true, avatarUrl, showAdminTools = false, showBusiness = false, whiteLabel = null, setNavLayout }: SidebarProps) {
  // Tooltip refs and hover state for bottom actions
  const upgradeRef = useRef<HTMLButtonElement | null>(null);
  const [upgradeHover, setUpgradeHover] = useState(false);
  // Theme toggle removed: retain theme for logo styling only
  const importRef = useRef<HTMLButtonElement | null>(null);
  const [importHover, setImportHover] = useState(false);
  const navToggleRef = useRef<HTMLButtonElement | null>(null);
  const [navToggleHover, setNavToggleHover] = useState(false);
  // toggleRef and hover state removed: collapse/expand button no longer shows a tooltip
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [portalPos, setPortalPos] = useState<{ left: number; bottom: number; width?: number } | null>(null);

  const lightActionIconClass = 'text-zinc-700';

  // White-label sidebar logos. Expanded uses the Sidebar Logo -> Primary Logo ->
  // Compact Logo fallback chain (theme-aware); collapsed always uses the compact
  // / logo mark (theme-aware).
  const sidebarExpandedLogoHref = resolveSidebarExpandedLogo(whiteLabel, theme);
  const sidebarCollapsedLogoHref = resolveSidebarCollapsedLogo(whiteLabel, theme);
  const configuredSidebarLogoBottomSpacing = Math.max(0, Number(whiteLabel?.sidebarLogoBottomSpacing) || 0);
  const tenantSidebarDividerTopMargin = SIDEBAR_DIVIDER_TOP_MARGIN_BY_TENANT[
    String(whiteLabel?.tenantSlug || '').trim().toLowerCase()
  ] || '';
  const tenantSidebarNavTopPadding = SIDEBAR_NAV_TOP_PADDING_BY_TENANT[
    String(whiteLabel?.tenantSlug || '').trim().toLowerCase()
  ] || '';
  const collapsedLogoScale = String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'gem-futures' ? 1.15 : 1;
  const expandedLogoScale = String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'prophub-trading' ? 0.95 : String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'nquarters' ? 0.95 : String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'trading-the-trend' ? 0.97 : String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'tori-trades' ? 0.95 : String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'prop-firm-kid' ? 0.8 : 1;
  const expandedLogoOffsetY = String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'nquarters' || String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'trading-the-trend' || String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'prop-firm-kid' || String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'campus-town-trading' ? 6 : String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'tori-trades' ? 4 : 0;

  // Close user menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      // Treat clicks inside the sidebar's userMenuRef OR inside the portal as inside clicks
      if (userMenuRef.current && userMenuRef.current.contains(target)) return;
      if (portalRef.current && portalRef.current.contains(target)) return;
      setUserMenuOpen(false);
    }
    if (userMenuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setUserMenuOpen(false);
    }
    if (userMenuOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [userMenuOpen]);

  // Listen to open/close user actions menu events for onboarding tour integration
  useEffect(() => {
    const onOpen = () => {
      if (collapsed) computePortalPos();
      setUserMenuOpen(true);
    };
    const onClose = () => setUserMenuOpen(false);
    window.addEventListener('dashboard:open-user-menu', onOpen);
    window.addEventListener('dashboard:close-user-menu', onClose);
    return () => {
      window.removeEventListener('dashboard:open-user-menu', onOpen);
      window.removeEventListener('dashboard:close-user-menu', onClose);
    };
  }, [collapsed]);

  // Compute portal position for collapsed menu so it renders next to the avatar button
  useEffect(() => {
    if (!userMenuOpen || !collapsed) return;
    function update() {
      const btn = userMenuRef.current?.querySelector('button');
      if (!btn) return setPortalPos(null);
      const r = (btn as HTMLElement).getBoundingClientRect();
      // position menu to the right of the button; align the BOTTOM of the menu with the button bottom
      const left = Math.ceil(r.right + 8);
      const bottom = Math.ceil(window.innerHeight - r.bottom);
      setPortalPos({ left, bottom, width: 224 });
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [userMenuOpen, collapsed]);

  // Synchronous compute helper used before opening the menu to prevent fallback render
  const computePortalPos = () => {
    const btn = userMenuRef.current?.querySelector('button');
    if (!btn) return;
    const r = (btn as HTMLElement).getBoundingClientRect();
    const left = Math.ceil(r.right + 8);
    const bottom = Math.ceil(window.innerHeight - r.bottom);
    setPortalPos({ left, bottom, width: 224 });
  };

  const firstMenuItemRef = useRef<HTMLButtonElement | null>(null);
  const toggleUserMenu = useCallback(() => setUserMenuOpen(o => !o), []);

  // Temporarily disable transitions on child elements during expand/collapse
  const [disableChildTransitions, setDisableChildTransitions] = useState(false);
  const resolvedDisplayName = getAccountDisplayName(displayName, email);

  // Previously auto-focused the first menu item on open; to avoid highlighting when opened by mouse,
  // we now only focus when opened via keyboard events (handled in handleUserButtonKey).

  const handleUserButtonKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Toggle menu and if opening, focus the first item for keyboard users
      if (!userMenuOpen && collapsed) computePortalPos();
      toggleUserMenu();
      if (!userMenuOpen) {
        setTimeout(() => firstMenuItemRef.current?.focus(), 0);
      }
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!userMenuOpen) {
        setUserMenuOpen(true);
      }
      setTimeout(() => firstMenuItemRef.current?.focus(), 0);
    }
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    (async () => {
      try {
        onLogoutStart?.();
        await signOut();
      } catch (err) {
        console.warn('Sign out failed', err);
      }
    })();
  };

  // Defensive cleanup: aggressively remove any unexpected pager (two-arrow button group) that may be injected.
  useEffect(() => {
    if (!collapsed) return; // only relevant in collapsed mode
    const scrollContainer = document.querySelector('.sidebar-root .flex-1.overflow-y-auto');
    if (!scrollContainer) return;

    const isChevronButton = (el: Element | null) => !!el && el.tagName === 'BUTTON' && /chevron|arrow/i.test(el.innerHTML);

    const nukePagers = () => {
      const groups = Array.from(scrollContainer.children) as HTMLElement[];
      groups.forEach(node => {
        // Candidates: tiny horizontal groups of 2-4 children containing mostly buttons with chevrons
        if (node.children.length && node.children.length <= 5) {
          const childArr = Array.from(node.children);
            const chevronBtnCount = childArr.filter(c => isChevronButton(c)).length;
            if (chevronBtnCount >= 2) {
              // Remove if visual size small (height <= 56) and width <= 140 to avoid legitimate content.
              const rect = node.getBoundingClientRect();
              if (rect.height <= 70 && rect.width <= 180) {
                node.remove();
              }
            }
        }
      });
    };

    nukePagers();

    const mo = new MutationObserver(() => nukePagers());
    mo.observe(scrollContainer, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [collapsed]);

  return (
    <aside
      className={`sidebar-root ${collapsed ? 'collapsed' : ''} ${disableChildTransitions ? 'no-transitions' : ''} sticky top-0 h-screen border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-[#1d1d20] p-4 hidden md:flex md:flex-col`}
      style={{ ['--sidebar-width']: collapsed ? '5rem' : '16rem', zIndex: 70 } as React.CSSProperties}
    >
      {collapsed ? (
        <div className="mb-1 sticky top-0 z-20 bg-white dark:bg-[#1d1d20] flex flex-col items-center">
          <div className="mt-0 mb-0 w-full overflow-visible flex justify-center">
            <TTLogo
              collapsed={collapsed}
              theme={theme}
              variant="stacked"
              expandedLogoHref={sidebarExpandedLogoHref}
              collapsedLogoHref={sidebarCollapsedLogoHref}
              collapsedLogoScale={collapsedLogoScale}
              expandedLogoScale={whiteLabel?.tenantSlug === 'bows' ? 1.4 : whiteLabel?.tenantSlug === 'bullish-bears' ? 0.9 : expandedLogoScale}
              expandedLogoCentered={whiteLabel?.tenantSlug === 'fullport-university' || whiteLabel?.tenantSlug === 'pjtrades'}
              textLogo={whiteLabel?.useTextLogo ? getTextLogoName(whiteLabel.companyName) : null}
              textLogoInitials={whiteLabel?.useTextLogo ? getTextLogoInitials(whiteLabel.companyName) : null}
            />
          </div>
          <div className="mt-3 flex w-full justify-center">
            <button
              aria-label="Toggle sidebar"
              title="Click to collapse/expand â€” hold Alt/Shift/Ctrl/Meta+click to switch to top menu"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                // briefly disable child transitions to avoid morphing
                setDisableChildTransitions(true);
                setTimeout(() => setDisableChildTransitions(false), 80);
                if (e && (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) && setNavLayout) {
                  try {
                    setNavLayout('top');
                    setCollapsed(false);
                  } catch {}
                  return;
                }
                setCollapsed(!collapsed);
              }}
              className="relative group w-7 h-7 flex items-center justify-center rounded-full active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900 shadow-sm dark:bg-gradient-to-br dark:from-zinc-800 dark:via-zinc-800 dark:to-zinc-700 dark:border dark:border-zinc-600/60 dark:hover:border-zinc-400/70 dark:hover:from-zinc-700 dark:hover:to-zinc-600 bg-white border border-zinc-300 hover:border-zinc-400 hover:bg-white/90"
            >
              {/* subtle inner ring for dark mode only */}
              <span className="absolute inset-0 rounded-full pointer-events-none ring-1 ring-transparent dark:ring-white/10" />
              <ChevronRight size={14} className="text-zinc-500 dark:text-zinc-300 group-hover:text-zinc-700 dark:group-hover:text-white transition-colors drop-shadow-sm" />
            </button>
          </div>
          {/* Tooltip for toggle removed per UX request */}
        </div>
      ) : (
        <div
          className="mb-1 flex items-center justify-between sticky top-0 z-20 bg-white dark:bg-[#1d1d20]"
          style={{ paddingBottom: `${configuredSidebarLogoBottomSpacing}px` }}
        >
          <div className="mt-0 mb-1 flex-1 min-w-0 pr-2 w-full">
            <TTLogo
              collapsed={collapsed}
              theme={theme}
              variant="stacked"
              expandedLogoHref={sidebarExpandedLogoHref}
              collapsedLogoHref={sidebarCollapsedLogoHref}
              expandedLogoOffsetY={String(whiteLabel?.tenantSlug || '').trim().toLowerCase() === 'gem-futures' ? 5 : expandedLogoOffsetY}
              expandedLogoScale={whiteLabel?.tenantSlug === 'bows' ? 1.2 : whiteLabel?.tenantSlug === 'bullish-bears' ? 0.9 : expandedLogoScale}
              expandedLogoCentered={whiteLabel?.tenantSlug === 'fullport-university' || whiteLabel?.tenantSlug === 'pjtrades'}
              textLogo={whiteLabel?.useTextLogo ? getTextLogoName(whiteLabel.companyName) : null}
              textLogoInitials={whiteLabel?.useTextLogo ? getTextLogoInitials(whiteLabel.companyName) : null}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              aria-label="Toggle sidebar"
              title="Click to collapse/expand â€” hold Alt/Shift/Ctrl/Meta+click to switch to top menu"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                // briefly disable child transitions to avoid morphing
                setDisableChildTransitions(true);
                setTimeout(() => setDisableChildTransitions(false), 80);
                if (e && (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) && setNavLayout) {
                  try {
                    setNavLayout('top');
                    setCollapsed(false);
                  } catch {}
                  return;
                }
                setCollapsed(!collapsed);
              }}
              className="relative group w-8 h-8 mt-2 flex items-center justify-center rounded-full active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900 shadow-sm dark:bg-gradient-to-br dark:from-zinc-800 dark:via-zinc-800 dark:to-zinc-700 dark:border dark:border-zinc-600/60 dark:hover:border-zinc-400/70 dark:hover:from-zinc-700 dark:hover:to-zinc-600 bg-white border border-zinc-300 hover:border-zinc-400 hover:bg-white/90"
            >
              {/* subtle inner ring for dark mode only */}
              <span className="absolute inset-0 rounded-full pointer-events-none ring-1 ring-transparent dark:ring-white/10" />
              <ChevronLeft size={16} className="text-zinc-500 dark:text-zinc-300 group-hover:text-zinc-700 dark:group-hover:text-white transition-colors drop-shadow-sm" />
            </button>
            {/* Tooltip for toggle removed per UX request */}
          </div>
        </div>
      )}
  {/* Divider below logo/header */}
  <div className={`${collapsed ? 'mt-2 mb-2' : `mb-5 mt-1 ${tenantSidebarDividerTopMargin}`} border-b border-zinc-300 dark:border-zinc-700 relative z-20`} />

  <div className={`${collapsed ? 'space-y-1 overflow-hidden' : `overflow-y-auto overflow-x-visible ${tenantSidebarNavTopPadding}`} flex-1 relative`} style={!collapsed ? { gap: '12px', display: 'flex', flexDirection: 'column' } : undefined} data-tour-target="top-navigation">
        {NAV_ITEMS.map(item => (
          <NavItem
            key={item.name}
            icon={item.icon}
            label={item.name === 'Reports' ? 'Trend' : item.name}
            active={active === item.name}
            collapsed={collapsed}
            onClick={() => onNavigate(item.name)}
            tourTarget={
              item.name === 'Dashboard'
                ? 'top-nav-dashboard'
                : item.name === 'Calendar'
                ? 'top-nav-calendar'
                : item.name === 'Reports'
                ? 'top-nav-trends'
                : item.name === 'Trades'
                ? 'top-nav-trades'
                : item.name === 'Journal'
                ? 'top-nav-journal'
                : undefined
            }
          />
        ))}
      </div>

      {/* Elevated UPGRADE button (hidden for now) */}
      <div className="mt-3 mb-5 relative hidden">
        {collapsed ? (
          <div className="flex items-center justify-center">
            {/* Only show the Upgrade button, no previous/next navigation button */}
            <button
              ref={upgradeRef}
              onMouseEnter={() => setUpgradeHover(true)}
              onMouseLeave={() => setUpgradeHover(false)}
              onFocus={() => setUpgradeHover(true)}
              onBlur={() => setUpgradeHover(false)}
              onClick={() => onNavigate('Reports')}
              aria-label="Upgrade"
              className="relative group w-10 h-10 rounded-2xl isolate active:scale-95 transition-transform"
            >
              {/* Glow gradient */}
              <span className="absolute -inset-px rounded-[1.15rem] bg-[conic-gradient(at_50%_-10%,#f0abfc,45%,#818cf8,75%,#22d3ee)] opacity-70 blur-sm dark:opacity-80" aria-hidden="true" />
              {/* Inner surface */}
              <span className="relative z-10 flex h-full w-full items-center justify-center rounded-[0.9rem] bg-white text-gray-900 shadow-sm border border-zinc-200 dark:border-zinc-700">
                <ShieldPlus className="w-4 h-4 text-gray-800" />
              </span>
            </button>
              {upgradeRef.current && (
              <Tooltip
                anchorRef={upgradeRef}
                open={upgradeHover}
                side="right"
                offset={8}
                customClassName={theme === 'dark' ? 'bg-blue-600 text-white border-blue-700' : ''}
              >
                Upgrade
              </Tooltip>
            )}
          </div>
        ) : (
          <button
            onClick={() => onNavigate('Reports')}
            className="relative w-full rounded-2xl isolate active:scale-95 transition-transform -mt-1"
          >
            {/* Glow gradient */}
            <span className="absolute -inset-px rounded-[1.15rem] bg-[conic-gradient(at_50%_-10%,#f0abfc,45%,#818cf8,75%,#22d3ee)] opacity-70 blur-sm dark:opacity-80" aria-hidden="true" />
            {/* Border gradient mask */}
            <span className="absolute inset-0 rounded-[1.05rem] p-[1px] [background:linear-gradient(90deg,#f0abfc,#818cf8,#22d3ee)] [mask:linear-gradient(#000,0,#000)_content-box,linear-gradient(#000,0,#000)] [mask-composite:exclude]" aria-hidden="true" />
            {/* Inner surface - 40px height when expanded (h-10) */}
            <span className="relative z-10 flex w-full items-center justify-center gap-2 rounded-[1rem] bg-white h-10 px-3 font-semibold text-sm leading-none text-gray-900 shadow-sm border border-zinc-200 dark:border-zinc-700">
              <ShieldPlus className="w-4 h-4 text-gray-800" />
              <span>UPGRADE</span>
            </span>
          </button>
        )}
      </div>

      {/* Quick actions stick to bottom of viewport (theme + import) */}
      <div className="mt-auto sticky bottom-0 pt-2 pb-2 bg-white dark:bg-[#1d1d20]">
        {/* Subtle gradient hint above actions (no shadows) */}
        <div className="h-4 bg-gradient-to-t from-white/80 to-transparent dark:from-zinc-900/80" />
        <div className={collapsed ? 'space-y-1' : 'space-y-4'}>
          {/* Import Trades */}
          <div>
            <button
              ref={importRef}
              onMouseEnter={() => setImportHover(true)}
              onMouseLeave={() => setImportHover(false)}
              onFocus={() => setImportHover(true)}
              onBlur={() => setImportHover(false)}
              onClick={() => onNavigate('Import Trades')}
              aria-label={collapsed ? 'IMPORT TRADES' : undefined}
              data-tour-target="user-menu-import-trades"
              className={`relative group ${collapsed ? 'w-10 h-10 rounded-2xl mx-auto text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700/50 hover:text-zinc-700 dark:hover:text-white' : 'w-full rounded-[18px] h-10 bg-blue-600 text-white hover:bg-blue-700'} flex items-center justify-center gap-2 active:scale-95 transition-colors`}
            >
              <CloudUpload className={`w-5 h-5 ${collapsed ? '' : 'text-white'}`} />
              {!collapsed && (
                <span className="font-semibold text-sm leading-none tracking-normal">Import Trades</span>
              )}
            </button>
            {/* Only show tooltip if sidebar is collapsed */}
            {collapsed && importRef.current && (
              <Tooltip
                anchorRef={importRef}
                open={importHover}
                side="right"
                offset={8}
                customClassName={theme === 'dark' ? 'bg-blue-600 text-white border-blue-700' : ''}
              >
                IMPORT TRADES
              </Tooltip>
            )}
          </div>



            {/* Two smaller actions beneath Import Trades: Menu + Light/Dark */}
            {collapsed ? (
              <div className="flex flex-col gap-1 px-0 items-center">
                {setNavLayout && (
                  <div className="flex items-center justify-center">
                    <button
                      ref={navToggleRef}
                      onMouseEnter={() => setNavToggleHover(true)}
                      onMouseLeave={() => setNavToggleHover(false)}
                      onFocus={() => setNavToggleHover(true)}
                      onBlur={() => setNavToggleHover(false)}
                      onClick={() => { try { setNavLayout('top'); setCollapsed(false); } catch {} }}
                      aria-label="Top Menu"
                      className="group w-10 h-10 rounded-2xl mx-auto flex items-center justify-center active:scale-95 transition-colors text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700/50 hover:text-zinc-700 dark:hover:text-white"
                    >
                      <PanelsTopLeftFilled className="w-5 h-5" />
                    </button>
                    {navToggleRef.current && (
                      <Tooltip
                        anchorRef={navToggleRef}
                        open={navToggleHover}
                        side="right"
                        offset={8}
                        customClassName={theme === 'dark' ? 'bg-blue-600 text-white border-blue-700' : ''}
                      >
                        TOP MENU
                      </Tooltip>
                    )}
                  </div>
                )}
                {canSwitchTheme ? (
                  <div className="flex items-center justify-center">
                    <ThemeToggle collapsed={true} compact theme={theme} setTheme={setTheme} />
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex gap-3">
                {setNavLayout ? (
                  <>
                    <button
                      ref={navToggleRef}
                      onMouseEnter={() => setNavToggleHover(true)}
                      onMouseLeave={() => setNavToggleHover(false)}
                      onFocus={() => setNavToggleHover(true)}
                      onBlur={() => setNavToggleHover(false)}
                      onClick={() => { try { setNavLayout('top'); setCollapsed(false); } catch {} }}
                      className={`group ${canSwitchTheme ? 'flex-1' : 'w-full'} h-10 rounded-[18px] flex items-center justify-center gap-2 active:scale-95 transition-all ${theme === 'light' ? 'bg-zinc-200 text-zinc-600 hover:bg-white hover:text-white border border-zinc-300' : 'bg-zinc-800/60 text-white hover:bg-zinc-700/70 border border-zinc-700/70'}`}
                    >
                      <PanelsTopLeftFilled className={`${ACTION_ICON} ${theme === 'light' ? lightActionIconClass + ' group-hover:text-blue-700' : 'text-white group-hover:text-blue-400'}`} />
                      <span className={`text-xs font-semibold tracking-wider ${theme === 'light' ? 'text-zinc-700 group-hover:text-blue-700' : 'text-white'}`}>{canSwitchTheme ? 'TOP-NAV' : 'TOP NAVIGATION'}</span>
                    </button>

                    {canSwitchTheme ? (
                      <div className="flex-1">
                        <ThemeToggle collapsed={false} compact theme={theme} setTheme={setTheme} />
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="w-full">
                    {canSwitchTheme ? <ThemeToggle collapsed={false} compact={false} theme={theme} setTheme={setTheme} /> : null}
                  </div>
                )}
              </div>
            )}
            {/* Widgets launcher now lives on the Dashboard header; sidebar button removed for consistency */}
          {/* Widgets launcher now lives on the Dashboard header; sidebar button removed for consistency */}
        </div>
      </div>

          {/* Divider above user menu */}
      <div className="mt-2 border-t border-zinc-300 dark:border-zinc-700" />

      {/* User menu / avatar */}
  <div className="mt-4 relative" ref={userMenuRef}>
          {collapsed ? (
          <div className="flex justify-center">
          <AvatarButton
            onClick={() => { if (!userMenuOpen && collapsed) computePortalPos(); toggleUserMenu(); }}
            onKeyDown={handleUserButtonKey}
            name={resolvedDisplayName}
            email={email}
            avatarUrl={avatarUrl}
            theme={theme}
            ariaLabel="User menu"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            showChevron={false}
            diameter={AVATAR_BUTTON_DIAMETERS.sideMenuCollapsed}
            badgeDiameter={AVATAR_CHEVRON_DIAMETERS.sideMenuCollapsed}
          />
          </div>
        ) : (
          <button
            ref={userMenuTriggerRef}
            onClick={toggleUserMenu}
            onKeyDown={handleUserButtonKey}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            data-tour-target="user-menu-button"
            className={`flex items-center justify-between gap-3 w-full bg-white dark:bg-zinc-800 ${userMenuOpen ? 'rounded-t-2xl rounded-b-none border-b-0 shadow-none' : 'rounded-2xl shadow-md hover:shadow-lg'} transition-all px-4 py-3 border border-zinc-200/60 dark:border-zinc-700/60 text-left`}
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <AvatarButton
                as="div"
                name={resolvedDisplayName}
                email={email}
                avatarUrl={avatarUrl}
                theme={theme}
                ariaLabel="User avatar"
                showChevron={false}
                diameter={AVATAR_BUTTON_DIAMETERS.sideMenu}
                badgeDiameter={AVATAR_CHEVRON_DIAMETERS.sideMenu}
              />
                  <div className="min-w-0 flex-1 text-center">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 leading-tight">{resolvedDisplayName || 'Account'}</h3>
                  </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-600 dark:text-gray-300 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
        {userMenuOpen && (
          // When collapsed, render the menu into a portal to avoid stacking/overflow issues.
          collapsed && portalPos ? createPortal(
            <div
              role="menu"
              aria-label="User menu"
              ref={portalRef}
              style={{ position: 'fixed', left: portalPos.left, bottom: portalPos.bottom, width: portalPos.width }}
              className="origin-bottom z-[99999]"
            >
              <div className={`border border-zinc-200/60 dark:border-zinc-700/60 bg-white/100 dark:bg-zinc-800/100 shadow-lg w-56 animate-in fade-in zoom-in-95 flex flex-col p-0 z-[99999] rounded-t-2xl rounded-b-xl`}>
                <button
                  ref={firstMenuItemRef}
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onNavigate('Settings'); }}
                  data-tour-target="user-menu-settings"
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                >
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </button>
                {showBusiness ? (
                  <button
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); onNavigate('Business'); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                  >
                    <Briefcase className="w-4 h-4" />
                    <span>Business</span>
                  </button>
                ) : null}
                {currentRuntime.brand.id === 'warrior-trading' ? (
                  <button
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); onNavigate('Challenges'); }}
                    data-tour-target="user-menu-challenges"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                  >
                    <Flag className="w-4 h-4" />
                    <span>Challenges</span>
                  </button>
                ) : null}
                {showAdminTools ? (
                  <button
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); onNavigate('Admin Tools'); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                  >
                    <ShieldPlus className="w-4 h-4" />
                    <span>Admin Tools</span>
                  </button>
                ) : null}
                <button
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onNavigate('Support'); }}
                  data-tour-target="user-menu-support"
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                >
                  <LifeBuoy className="w-4 h-4" />
                  <span>Support</span>
                </button>
                {/* Divider sits flush above Logout, no margin */}
                <div className="border-t border-zinc-200 dark:border-zinc-700 m-0 p-0" style={{marginTop: 0, marginBottom: 0}} />
                <button
                  role="menuitem"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 focus:outline-none focus:bg-red-50 dark:focus:bg-red-500/10 rounded-b-xl"
                  style={{ marginTop: 0, marginBottom: 0, borderBottomLeftRadius: '0.75rem', borderBottomRightRadius: '0.75rem' }}
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>,
            document.body
          ) : (
            // Fallback: non-collapsed / inline rendering
            <div
              role="menu"
              aria-label="User menu"
              className={`origin-bottom w-full -mt-px`}
            >
              <div
                className="border border-zinc-200/60 dark:border-zinc-700/60 bg-white/100 dark:bg-zinc-800/100 shadow-lg w-full animate-in fade-in zoom-in-95 flex flex-col p-0 rounded-b-xl"
              >
                <button
                  ref={firstMenuItemRef}
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onNavigate('Settings'); }}
                  data-tour-target="user-menu-settings"
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                >
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </button>
                {showBusiness ? (
                  <button
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); onNavigate('Business'); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                  >
                    <Briefcase className="w-4 h-4" />
                    <span>Business</span>
                  </button>
                ) : null}
                {currentRuntime.brand.id === 'warrior-trading' ? (
                  <button
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); onNavigate('Challenges'); }}
                    data-tour-target="user-menu-challenges"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                  >
                    <Flag className="w-4 h-4" />
                    <span>Challenges</span>
                  </button>
                ) : null}
                {showAdminTools ? (
                  <button
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); onNavigate('Admin Tools'); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                  >
                    <ShieldPlus className="w-4 h-4" />
                    <span>Admin Tools</span>
                  </button>
                ) : null}
                <button
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onNavigate('Support'); }}
                  data-tour-target="user-menu-support"
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 focus:outline-none focus:bg-zinc-100 dark:focus:bg-zinc-700/60"
                >
                  <LifeBuoy className="w-4 h-4" />
                  <span>Support</span>
                </button>
                {/* Divider sits flush above Logout, no margin */}
                <div className="border-t border-zinc-200 dark:border-zinc-700 m-0 p-0" style={{marginTop: 0, marginBottom: 0}} />
                <button
                  role="menuitem"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 focus:outline-none focus:bg-red-50 dark:focus:bg-red-500/10 rounded-b-xl"
                  style={{ marginTop: 0, marginBottom: 0, borderBottomLeftRadius: '0.75rem', borderBottomRightRadius: '0.75rem' }}
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </aside>
  );
}

// Extracted theme toggle to keep Sidebar component lean

function ThemeToggle({
  collapsed,
  compact,
  theme,
  setTheme,
}: {
  collapsed: boolean;
  compact?: boolean;
  theme: 'light' | 'dark';
  setTheme: (t: 'light' | 'dark') => void;
}) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [hover, setHover] = useState(false);
  const isLight = theme === 'light';
  const actionLabel = isLight ? 'DARK' : 'LIGHT';

  const lightActionIconClass = 'text-zinc-700';
  const lightActionButtonClass = 'bg-zinc-200 text-zinc-600 hover:bg-zinc-900 hover:text-white border border-zinc-200';

  if (compact && collapsed) {
    return (
      <>
        <button
          ref={ref}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setHover(true)}
          onBlur={() => setHover(false)}
          onClick={() => setTheme(isLight ? 'dark' : 'light')}
          aria-label={actionLabel}
          className={`group w-10 h-10 rounded-2xl mx-auto flex items-center justify-center active:scale-95 transition-colors ${isLight ? 'text-zinc-700 hover:bg-zinc-200 hover:text-zinc-700' : 'text-zinc-300 hover:bg-zinc-700/50 hover:text-white'}`}
        >
            {isLight ? (
            <MoonIcon className={`w-5 h-5 ${lightActionIconClass} fill-current group-hover:text-yellow-500`} />
            ) : (
            <LightbulbSolid className={`w-5 h-5 transform transition-transform duration-200 text-zinc-400 group-hover:text-yellow-400 group-hover:scale-125`} />
          )}
        </button>
        {ref.current && (
          <Tooltip
            anchorRef={ref}
            open={hover}
            side="right"
            offset={8}
            customClassName={theme === 'dark' ? 'bg-blue-600 text-white border-blue-700' : ''}
          >
            {actionLabel}
          </Tooltip>
        )}
      </>
    );
  }

  if (compact && !collapsed) {
    return (
      <button
        ref={ref}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        onClick={() => setTheme(isLight ? 'dark' : 'light')}
        className={`group w-full h-10 rounded-[18px] flex items-center justify-center gap-2 active:scale-95 transition-all ${isLight ? lightActionButtonClass : 'bg-zinc-800/60 text-zinc-400 border border-zinc-700/70 hover:bg-zinc-700 hover:text-white'}`}
      >
        {/* Use a small icon to match the reference */}
        {isLight ? (
          <>
            <MoonIcon className={`${ACTION_ICON} ${lightActionIconClass} fill-current group-hover:text-yellow-500`} />
            <span className="ml-2 text-xs font-semibold tracking-wider text-zinc-600 group-hover:text-white">DARK</span>
          </>
            ) : (
          <>
            <LightbulbSolid className={`${ACTION_ICON} transform transition-transform duration-200 text-zinc-400 group-hover:text-yellow-500 group-hover:scale-100`} />
            <span className="ml-2 text-xs font-semibold tracking-wider text-white">LIGHT</span>
          </>
        )}
      </button>
    );
  }
  return (
    <>
      <button
        ref={ref}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
  onClick={() => setTheme(isLight ? 'dark' : 'light')}
  aria-label={collapsed ? actionLabel : undefined}
        className={`relative group ${collapsed ? 'w-10 h-10 rounded-2xl mx-auto' : 'w-full rounded-[18px] h-10 font-semibold text-sm leading-none'} flex items-center justify-center gap-2 active:scale-95 transition-all shadow-sm ${isLight ? lightActionButtonClass : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700 hover:text-white'}`}
      >
        <span className={`relative inline-block ${collapsed ? 'w-5 h-5' : 'w-4 h-4'}`}>
          {/* Light theme (Moon) visible when isLight */}
          <MoonIcon className={`absolute inset-0 ${collapsed ? 'w-5 h-5' : ACTION_ICON} transition-all duration-300 ease-out ${isLight ? 'opacity-100 scale-100 rotate-0 text-zinc-400 fill-current group-hover:text-yellow-500' : 'opacity-0 scale-75 -rotate-45'} `} />
          {/* Dark theme (Lightbulb) visible when !isLight */}
          <LightbulbSolid
            className={`absolute inset-0 ${collapsed ? 'w-5 h-5' : ACTION_ICON} transform transition-all duration-300 ease-out ${!isLight ? 'opacity-100 scale-100 rotate-0 text-zinc-400 group-hover:text-yellow-500 group-hover:scale-125' : 'opacity-0 scale-75 rotate-45'}`}
          />
        </span>
        {!collapsed && (
          <span className="relative overflow-hidden inline-flex">
            <span className={`inline-block font-semibold text-sm leading-none tracking-normal transition-all duration-300`}>{isLight ? 'DARK MODE' : 'LIGHT MODE'}</span>
          </span>
        )}
      </button>
      {collapsed && ref.current && (
        <Tooltip
          anchorRef={ref}
          open={hover}
          side="right"
          offset={8}
          customClassName={theme === 'dark' ? 'bg-blue-600 text-white border-blue-700' : ''}
        >
          {actionLabel}
        </Tooltip>
      )}
    </>
  );
}
